import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { isEqual } from 'lodash';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import CardActions from '@material-ui/core/CardActions';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';

// import utils
import { openOriginalImage, logger } from './../../../utils/utils';

// import material-icon
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import SupervisorAccountOutlinedIcon from '@material-ui/icons/SupervisorAccountOutlined';
import SettingsIcon from '@material-ui/icons/Settings';

import { FormComment } from '../FormComment';

import * as file from '../../../data/file';
import { useThumbnails } from '../../../hooks/useFile';

import GridAvatar from './../../fragments/GridAvatar';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    overflow: 'hidden',
    padding: theme.spacing(2, 0),
  },
  card: {
    minWidth: 250,
    maxWidth: 930,
  },
  // bullet: {
  //   display: 'inline-block',
  //   margin: '0 2px',
  //   transform: 'scale(0.8)',
  // },
  title: {
    fontSize: 14,
  },
  pos: {
    marginBottom: 12,
  },
  contentWidth: {
    maxWidth: 860,
  },
  formCommentWidth: {
    maxWidth: 890,
  },
  breakWord: {
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
    MozWhiteSpace: 'pre-wrap',
  },
  // Style thumb-image
  thumbsContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 16,
  },

  thumb: {
    //display: 'inline-flex',
    borderRadius: 2,
    border: '1px dashed #eaeaea',
    marginBottom: 20,
    marginRight: 8,
    maxWidth: 500,
    // minHeight: 150,
    padding: 4,
    boxSizing: 'border-box',
  },
  thumbInner: {
    display: 'flex',
    minWidth: 0,
    overflow: 'hidden',
  },

  img: {
    display: 'block',
    [theme.breakpoints.up('xs')]: {
      width: '100%',
    },
    [theme.breakpoints.up('sm')]: {
      width: '100%',
    },
    [theme.breakpoints.up('md')]: {
      maxWidth: 500,
    },
    [theme.breakpoints.up('lg')]: {
      maxWidth: 500,
    },
    height: '100%',
  },
}));

const linkOriginalImage = (issue_id, file_id) => {
  file
    .original({ issue_id, file_id })
    .then((data) => {
      openOriginalImage(data.content);
    })
    .catch((error) => alert('Error linkOriginalImage'));
};

/**
 * CommentCard() (Conponent)
 * @param {object} props
 * data props
 * @param {int} props.id, [issue-] comment-id
 * @param {int} props.issueId, issue id
 * @param {int} props.issueCommentTypeId, comment-type-id
 * @param {string} props.issueCommentTypeName, comment-type-name
 * @param {string} props.issueCommentTypeDescription, comment-type-description
 * @param {int} props.receiverType, receiver-type (0, 1)
 * @param {int} props.detail, comment-detail
 * @param {int} props.commentFiles, string fileIds with , delimitor
 * @param {string} creater, comment-creater (createBy)
 * @param {string} createDate, string create-date
 * @param {string} modifier, comment-modifier (modifyBy)
 * @param {string} modifyDate, string modify-date
 * feature props
 * @param {bool} editing, [default: false] Set true if user can edit issue;
 * @param {bool} receiverTapping defalut: false
 * event props
 * @param {func} onDroppedFiles, function(filesBase64: array, data: object) => void
 * @param {func} onRemovedFile,
 * @param {func}  onSubmit, function(dataUpdated) => void
 * @return
 */
const CommentCard = ({
  // data props
  id = undefined,
  issueId = '',
  issueCommentTypeId = -1,
  issueCommentTypeName = null,
  issueCommentTypeDescription = null,
  receiverType = null,
  detail = '',
  commentFiles = null,
  creater = '',
  avatar = {
    image: undefined,
    name: undefined,
  },
  createDate = '',
  modifier = '',
  modifyDate = '',
  // feature props
  editing,
  receiverTapping = false,
  // event props
  onDroppedFiles,
  onRemovedFile,
  onSubmit,
}) => {
  const classes = useStyles();

  logger('CommentCard render');

  const [isEdit, setIsEdit] = useState(false);

  const thumbnails = useThumbnails(issueId, commentFiles);

  const handleCancel = () => {
    setIsEdit(false);
  };

  const handleSubmit = async (data) => {
    if (onSubmit) {
      await onSubmit(data);
    }
    setIsEdit(false);
  };

  const handleDroppedFiles = (newFiles, extendNewFiles) => {
    if (onDroppedFiles) {
      onDroppedFiles(newFiles, extendNewFiles);
    }
  };
  const cardHeader = {
    action: (
      <React.Fragment>
        {!isEdit && editing && (
          <Box>
            <Tooltip title="แก้ไข">
              <IconButton
                aria-label="edit issue"
                size="small"
                onClick={() => {
                  setIsEdit(true);
                }}
              >
                <EditOutlinedIcon style={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </React.Fragment>
    ),
    title: (
      <Box
        display="flex"
        flexWrap="wrap"
        bgcolor="background.paper"
        css={{ maxWidth: '100%' }}
        alignItems="center"
      >
        {' '}
        <Box pr={1}>
          <Typography variant="body2" color="textPrimary" gutterBottom>
            {creater}
          </Typography>
        </Box>
        <Box pr={1}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            เมื่อ: {createDate}
          </Typography>
        </Box>
        {receiverType === 1 && (
          <React.Fragment>
            <Box>
              <Tooltip title="ความคิดเห็นภายใน">
                <SupervisorAccountOutlinedIcon fontSize="small" />
              </Tooltip>
            </Box>
          </React.Fragment>
        )}
      </Box>
    ),
    subheader:
      !isEdit && receiverType === 1 && issueCommentTypeName ? (
        <Tooltip title={issueCommentTypeDescription}>
          <Typography variant="subtitle2" gutterBottom>
            {issueCommentTypeId > 0 && (
              <SettingsIcon
                style={{ verticalAlign: 'middle' }}
                fontSize="small"
              />
            )}{' '}
            {issueCommentTypeName}
          </Typography>
        </Tooltip>
      ) : null,
  };
  return (
    <GridAvatar image={avatar.image} name={avatar.name}>
      {!isEdit && (
        <Card className={classes.card} variant="outlined" square={false}>
          <CardHeader
            action={cardHeader.action}
            title={cardHeader.title}
            subheader={cardHeader.subheader}
          />
          <CardContent>
            <div>
              <Typography
                className={classes.pos}
                variant="body1"
                component="div"
              >
                <div className={clsx(classes.breakWord, classes.contentWidth)}>
                  {detail}
                </div>
              </Typography>
              {/* Apply thumbs style from Dropzone preview component */}
              <aside className={classes.thumbsContainer}>
                {thumbnails.map((thumbnail, index) => (
                  <div className={classes.thumb} key={index}>
                    <div className={classes.thumbInner}>
                      <img
                        src={thumbnail.thumbnail}
                        className={classes.img}
                        onClick={() =>
                          linkOriginalImage(thumbnail.issue_id, thumbnail.id)
                        }
                        style={{ cursor: 'pointer' }}
                      />
                    </div>
                  </div>
                ))}
              </aside>
            </div>
          </CardContent>

          {modifyDate && modifier && (
            <CardActions>
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ width: '100%' }}
              >
                <Box
                  display="flex"
                  flexWrap="wrap"
                  justifyContent="flex-start"
                  bgcolor="background.paper"
                >
                  <Box pt={0.5} pr={1}>
                    แก้ไขล่าสุด เมื่อ: {modifyDate}
                  </Box>
                  <Box pt={0.5} pr={1}>
                    โดย: {modifier}
                  </Box>
                </Box>
              </Typography>
            </CardActions>
          )}
        </Card>
      )}
      {isEdit && (
        <div className={clsx(classes.formStyle)}>
          <FormComment
            id={id}
            cardHeader={cardHeader}
            issueCommentTypeId={issueCommentTypeId}
            receiverType={receiverType}
            detail={detail}
            elevation={0}
            variant="outlined"
            receiverTapping={receiverTapping}
            existFiles={thumbnails}
            onDroppedFiles={handleDroppedFiles}
            onRemovedFile={onRemovedFile}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            formControlStyle={classes.formCommentWidth}
          />
        </div>
      )}
    </GridAvatar>
  );
};

/**
 * CommentCard's propTypes
 */
CommentCard.propTypes = {
  id: PropTypes.string,
  issueId: PropTypes.string,
  issueCommentTypeId: PropTypes.number,
  receiverType: PropTypes.number,
  detail: PropTypes.string,
  commentFiles: PropTypes.string,
  creater: PropTypes.string,
  avatar: PropTypes.object,
  createDate: PropTypes.string,
  modifier: PropTypes.string,
  modifyDate: PropTypes.string,
  receiverTapping: PropTypes.bool,
  onDroppedFiles: PropTypes.func,
  onRemovedFile: PropTypes.func,
  onSubmit: PropTypes.func,
};

/**
 * By default it will only shallowly compare complex objects in the props object.
 * If you want control over the comparison,
 * you can also provide a custom comparison function as the second argument.
 * https://reactjs.org/docs/react-api.html
 * https://stackoverflow.com/questions/63502588/avoid-rerendering-child-when-passing-object-in-props
 */
export default React.memo(CommentCard, isEqual);
